import {
  AUTOCOMPLETE_LISTBOX_HEIGHT,
  AUTOCOMPLETE_STYLES,
  ICON_STYLES
} from './OrganizationSearchField.constants'
import { Autocomplete, Box } from '@mui/material'
import { Icon, Text } from '@dtx-company/shared-components/src'
import {
  InvitationPrompt,
  SearchFieldProps,
  SearchFor,
  SearchResult
} from './OrganizationSearchField.types'
import { LoadingSpinner } from '@dtx-company/app/shared/code/src/components/utils/loading-spinner'
import { MemberNameWithInitials, TeamNameWithInitials } from '../NameWithInitials'
import { Team, TeamMember } from '@dtx-company/true-common/src/types/org'
import {
  getOptionLabel,
  getSearchResultKey,
  getTeamMemberFromInvitationPrompt,
  searchResultIsInvitationPrompt,
  searchResultIsTeam,
  searchResultIsTeamMember
} from './OrganizationSearchField.utils'
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined'
import React, { useEffect, useRef } from 'react'

export const SearchField: React.FC<SearchFieldProps> = ({
  label,
  loading,
  placeholder,
  results,
  inputValue,
  setInputValue,
  onResultClicked,
  onRefInitialized,
  searchFor,
  onFocus,
  fromInternalAdmin,
  inputType
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (ref.current) {
      const input = ref.current.getElementsByTagName('input')[0]
      onRefInitialized?.(input)
    }
  }, [ref, onRefInitialized])

  return (
    <Autocomplete
      sx={AUTOCOMPLETE_STYLES}
      id="org-member-team-search"
      noOptionsText="No results"
      ListboxProps={{
        style: {
          maxHeight: AUTOCOMPLETE_LISTBOX_HEIGHT
        }
      }}
      options={results}
      filterOptions={x => x}
      filterSelectedOptions
      getOptionLabel={getOptionLabel}
      renderTags={() => null}
      inputValue={inputValue}
      onChange={(_, value, reason) => {
        if (reason === 'selectOption') {
          onResultClicked(value as SearchResult)
        }
      }}
      onInputChange={event => {
        if (event) {
          setInputValue((event.target as HTMLInputElement).value)
        }
      }}
      onFocus={() => {
        onFocus?.()
      }}
      onClose={() => setInputValue('')}
      autoSelect={false}
      renderOption={(props, result: SearchResult, { inputValue }) => {
        return (
          <li {...props} key={getSearchResultKey(result)} data-testid="search-result">
            <Box sx={{ py: '4px' }}>
              <SearchResultItem searchResult={result} searchString={inputValue} />
            </Box>
          </li>
        )
      }}
      renderInput={params => {
        return (
          <Box ref={ref}>
            {inputType !== 'v2' && <label>{label}</label>}
            <Box
              sx={{
                '&:hover': { '& input': { borderColor: 'black' } },
                '& input': { fontSize: '16px' }
              }}
              ref={params.InputProps.ref}
              position="relative"
            >
              <Box sx={ICON_STYLES}>
                {loading ? (
                  <LoadingSpinner
                    {...(fromInternalAdmin && { iconSrc: '/internal-admin/icons/spinner.svg' })}
                  />
                ) : (
                  <>
                    {inputType === 'v2' ? (
                      <PersonAddAlt1OutlinedIcon />
                    ) : (
                      <SearchIcon searchFor={searchFor} fromInternalAdmin={fromInternalAdmin} />
                    )}
                  </>
                )}
              </Box>
              <input
                placeholder={inputType === 'v2' ? label : placeholder}
                type="text"
                aria-label={label || placeholder}
                {...params.inputProps}
              />
            </Box>
          </Box>
        )
      }}
    />
  )
}

const SearchIcon = ({
  searchFor,
  fromInternalAdmin
}: {
  searchFor: SearchFor
  fromInternalAdmin?: boolean
}): JSX.Element => {
  const iconSrc = searchFor === 'teams' ? '/icons/search.svg' : '/icons/add-user.svg'
  const internalAdminSrc = '/internal-admin/icons/add-user.svg'
  return (
    <Icon
      color="black"
      src={fromInternalAdmin ? internalAdminSrc : iconSrc}
      alt=""
      width="24px"
      height="24px"
    />
  )
}

const SearchResultItem: React.FC<{ searchResult: SearchResult; searchString?: string }> = ({
  searchResult,
  searchString
}) => {
  if (searchResultIsTeamMember(searchResult)) {
    return (
      <MemberNameWithInitials member={searchResult as TeamMember} boldSubstring={searchString} />
    )
  }
  if (searchResultIsTeam(searchResult)) {
    return <TeamNameWithInitials team={searchResult as Team} boldSubstring={searchString} />
  }
  if (searchResultIsInvitationPrompt(searchResult)) {
    const invitationPrompt = searchResult as InvitationPrompt
    const pendingMember = getTeamMemberFromInvitationPrompt(invitationPrompt)
    return (
      <Box
        data-testid="invitation-prompt"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <MemberNameWithInitials member={pendingMember} />
        <Text fontSize="14px" minWidth="25px" maxWidth="25px" px="8px" color="primary.flowBlue">
          Invite
        </Text>
      </Box>
    )
  }
  return <></>
}
